import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { useMediaQuery } from "react-responsive";
import { motion } from "framer-motion";
import cn from "classnames";
import { useRouter } from "next/router";
import { When } from "@/components/common/When";
import { useOnClickOutside } from "@/hooks";
import useGenerateHomeQr from "@/components/page-modules/home-page/hooks/useGenerateHomeQr";
import { IHomePageTypeButton } from "@/types/stateTypes";
import { destroyAllState, getHomePageTypeLimit } from "@/helper/helper";
import { useHomePageTypesButton, useIsloading, useRegisterRequired } from "@/states/ui.state";
import { useConfig } from "@/states/common.state";
import { typesRequiredRegister } from "@/components/page-modules/home-page/GenerateTypes/PreviewResult/data/preview-result.repository";
import { MAX_TYPE_LENGTH } from "@/constant";
import { solutions } from "@/components/common/solutions";
import { useSolutionsSelect } from "@/states/solution.state";
import { IoIosArrowForward } from "react-icons/io";
import { useDownloadClickedState } from "@/states/user.state";
import style from "./TypesButton.module.scss";
import { QR_TYPES } from "@/constant/qr_type.enum";
import Skeleton from "react-loading-skeleton";
const TypesButton = () => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const {
    setIsloading
  } = useIsloading();
  const {
    setPayload,
    setFiles
  } = useConfig();
  const {
    filteredHomePageTypesButton,
    setFilterHomePageTypesButton
  } = useHomePageTypesButton();
  const [setSolutionsType, solutionsSelectedTypes] = useSolutionsSelect(state => [state.setSolutionsType, state.solutionsSelectedTypes]);
  const {
    mutateHomeQr
  } = useGenerateHomeQr(false);
  const [showedButtons, setShowedButtons] = useState<IHomePageTypeButton[]>([]);
  const [selectboxButtons, setSelectBoxTypes] = useState<IHomePageTypeButton[]>([]);
  const [isOptionClicked, setIsOptionClicked] = useState(false);
  const [isDrop, setIsDrop] = useState(false);
  const {
    setDownloadClicked
  } = useDownloadClickedState();
  const isTablet = useMediaQuery({
    maxWidth: 1280
  });
  const {
    setIsTypeRequireRegister
  } = useRegisterRequired();
  const slug = router.query?.slug;
  useEffect(() => {
    if (router.pathname.includes("dashboard")) return;
    const selectedType = getSelectedType("url");
    if (!selectedType) return;
    setSolutionsType({
      solutionSelectedType: selectedType.type
    });
    setFilterHomePageTypesButton(selectedType.type);
  }, []);
  const getSelectedType = (type: string) => filteredHomePageTypesButton.find(item => item.type === type) || null;
  useEffect(() => {
    const limit = getHomePageTypeLimit(isTablet);
    let updatedHomePageTypes = [...filteredHomePageTypesButton];
    if (isOptionClicked) {
      updatedHomePageTypes = [...showedButtons, ...selectboxButtons];
    }
    if (slug && !isOptionClicked) {
      updatedHomePageTypes = reorderTypesBasedOnSlug(updatedHomePageTypes, slug as string);
    }
    setShowedButtons(updatedHomePageTypes.slice(0, limit));
    setSelectBoxTypes(updatedHomePageTypes.slice(limit, MAX_TYPE_LENGTH));
  }, [slug, isTablet]);
  const reorderTypesBasedOnSlug = (types: IHomePageTypeButton[], slug: string) => {
    const typeFromURL = solutions.find(item => item.route === `/${slug}`) || solutions[0];
    const selectedIndex = types.findIndex(item => item.type === typeFromURL.type);
    if (selectedIndex === -1) return types;
    const selectedType = types[selectedIndex];
    types.splice(selectedIndex, 1);
    setSolutionsType({
      solutionSelectedType: typeFromURL.type
    });
    return [selectedType, ...types];
  };
  useOnClickOutside(dropdownRef, () => setIsDrop(false));
  const handleClickChangeType = (type: string) => {
    const selectedButton = getSelectedType(type);
    if (selectedButton) {
      const redirectType = selectedButton.type === QR_TYPES.MENU ? {
        redirect_type: "url"
      } : undefined;
      mutateHomeQr({
        params: {
          ...redirectType
        }
      });
      destroyAllState("on", true, true);
      setDownloadClicked(false);
      setIsloading(false);
      setPayload(null);
      setFiles({});
      setSolutionsType({
        solutionSelectedType: type,
        title: selectedButton.landingPageTitle
      });
      setIsTypeRequireRegister(typesRequiredRegister.includes(type));
    }
    ;
  };
  const handleClick = (item: IHomePageTypeButton) => {
    setIsOptionClicked(true);
    handleClickChangeType(item.type);
    updateButtonStates(item);
  };
  const findButtonByType = (buttons: IHomePageTypeButton[] | undefined, type: string): IHomePageTypeButton | undefined => {
    return buttons?.find(button => button.type === type);
  };
  useEffect(() => {
    if (solutionsSelectedTypes !== "text") return;
    const selectedButton = findButtonByType(selectboxButtons, solutionsSelectedTypes);
    if (selectedButton) {
      handleClick(selectedButton);
      return;
    }
    if (findButtonByType(showedButtons, solutionsSelectedTypes)) {
      handleClickChangeType("text");
    }
  }, [solutionsSelectedTypes]);
  const updateButtonStates = (item: IHomePageTypeButton) => {
    let updatedShowedButtons = [...showedButtons];
    let updatedSelectboxButtons = [...selectboxButtons];
    const itemIndex = updatedSelectboxButtons.findIndex(button => button.type === item.type);
    updatedSelectboxButtons.splice(itemIndex, 1);
    const fn = isOptionClicked ? swapFirstButton : swapLastButton;
    fn(updatedShowedButtons, updatedSelectboxButtons, item);
    setShowedButtons(updatedShowedButtons);
    setSelectBoxTypes(updatedSelectboxButtons);
  };
  const swapFirstButton = (showed: IHomePageTypeButton[], selectbox: IHomePageTypeButton[], item: IHomePageTypeButton) => {
    const [firstButton] = showed;
    showed[0] = item;
    selectbox.unshift(firstButton);
  };
  const swapLastButton = (showed: IHomePageTypeButton[], selectbox: IHomePageTypeButton[], item: IHomePageTypeButton) => {
    const lastButton = showed[showed.length - 1];
    selectbox.unshift(lastButton);
    showed.unshift(item);
    showed.pop();
  };
  return <div className={style.wrapper} data-sentry-component="TypesButton" data-sentry-source-file="TypesButton.tsx">
      <div className={style.list}>
        {showedButtons.map(({
        type,
        landingPageTitle
      }) => <button key={type} className={cn(style.item, {
        [style.active]: type === solutionsSelectedTypes
      })} onClick={() => {
        handleClickChangeType(type);
      }} data-cy="typesButton">
            <div className={style.itemInner}>
              <div className={cn(style.mainIcon, style.icon)}>
                <Image width={18} height={18} src={`/types/${type}.svg`} alt="Type icon" />
              </div>
              <div className={cn(style.hoverIcon, style.icon)}>
                <Image width={18} height={18} src={`/types/filled/${type}.svg`} alt="Hover type icon" />
              </div>
            </div>
            <span>{landingPageTitle}</span>
          </button>)}
        <div className={style.moreBtn} ref={dropdownRef} data-cy="more" onClick={() => setIsDrop(!isDrop)}>

          <motion.div className={style.dropdownToggleIcon} animate={{
          rotate: isDrop ? 270 : 90
        }} data-sentry-element="unknown" data-sentry-source-file="TypesButton.tsx">
            <IoIosArrowForward color="var(--color-primary)" size={16} data-sentry-element="IoIosArrowForward" data-sentry-source-file="TypesButton.tsx" />
          </motion.div>
          <When condition={isDrop} data-sentry-element="When" data-sentry-source-file="TypesButton.tsx">
            <div className={style.dropdownContainer}>
              <motion.ul animate={{
              height: isDrop ? 300 : 0,
              opacity: isDrop ? 1 : 0,
              visibility: isDrop ? "visible" : "hidden"
            }} data-sentry-element="unknown" data-sentry-source-file="TypesButton.tsx">
                {selectboxButtons.map(item => <li key={item.type} onClick={() => {
                handleClick(item);
              }}>
                    <Image width={16} height={16} src={`/types/${item.type}.svg`} alt="Type icon" />
                    <span>{item.landingPageTitle}</span>
                  </li>)}
              </motion.ul>
            </div>
          </When>
        </div>
      </div>
    </div>;
};
export default TypesButton;